import { useEffect } from "react";
import { AttentionNotification, stripTitleLabel, titlePrefix, withTitleLabel } from "@/lib/workflowAttention";

// Deep end of --brand (index.css): --primary into the final violet stop, since the light coral start washed out at 16px
const DOT_STOPS = ["#a43d93", "#843ee3"] as const;
const BASE_ICON = "/favicon-32x32.png";

let badgeCache: string | null = null;

function renderBadge(): Promise<string> {
  if (badgeCache) return Promise.resolve(badgeCache);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = canvas.height = 32;
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("no canvas"));
      ctx.drawImage(img, 0, 0, 32, 32);
      // Filled corner dot, large enough to spot in a tab strip while leaving the J readable
      const [cx, cy, r] = [24.5, 7.5, 7];
      const gradient = ctx.createLinearGradient(cx - r, cy - r, cx + r, cy + r);
      gradient.addColorStop(0, DOT_STOPS[0]);
      gradient.addColorStop(1, DOT_STOPS[1]);
      ctx.beginPath();
      ctx.arc(cx, cy, r + 1.5, 0, Math.PI * 2);
      ctx.fillStyle = "#FFFFFF";
      ctx.fill();
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fillStyle = gradient;
      ctx.fill();
      const url = canvas.toDataURL("image/png");
      badgeCache = url;
      resolve(url);
    };
    img.onerror = reject;
    img.src = BASE_ICON;
  });
}

function iconLinks(): HTMLLinkElement[] {
  return Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]'));
}

function setIcons(url: string | null) {
  for (const link of iconLinks()) {
    if (!link.dataset.originalHref) link.dataset.originalHref = link.getAttribute("href") ?? "";
    link.href = url ?? link.dataset.originalHref;
  }
}

/** Mirrors pending attention into the browser tab: title label plus a brand favicon dot. Callers pass only this tab's project. */
export function useAttentionIndicators(items: AttentionNotification[]) {
  const prefix = titlePrefix(items);
  const badged = items.length > 0;

  useEffect(() => {
    const apply = () => {
      const next = withTitleLabel(prefix, document.title);
      if (document.title !== next) document.title = next;
    };
    apply();
    if (!prefix) return;
    // react-helmet rewrites <title> on navigation; re-apply the prefix whenever it does
    const observer = new MutationObserver(apply);
    observer.observe(document.head, { subtree: true, childList: true, characterData: true });
    return () => {
      observer.disconnect();
      document.title = stripTitleLabel(document.title);
    };
  }, [prefix]);

  useEffect(() => {
    if (!badged) {
      setIcons(null);
      return;
    }
    let cancelled = false;
    renderBadge()
      .then((url) => {
        if (!cancelled) setIcons(url);
      })
      .catch(() => {
        // Favicon is decorative; the title prefix still signals attention
      });
    return () => {
      cancelled = true;
    };
  }, [badged]);
}
